import baustein from "baustein";
import { BREAKPOINTS } from "web/react/constants";
import analytics from "web/script/analytics/analytics";
import globals from "web/script/modules/globals";
import { getLanguageWithoutCountry } from "web/script/modules/language";
import { addVisibilityWatcher } from "web/script/modules/visibility-watcher";
import googleCsa from "web/script/embedded/google-adsense";
import onAdLoaded from "web/script/utils/ad-analytics";
import browser from "web/script/utils/browser";

const channelIdMap = {
    "oos-pdp": { mobile: "1598572231", desktop: "8791081831" },
    "instock-pdp": { mobile: "8526409836", desktop: "7049676638" },
    "clp-top": { mobile: "8286600639", desktop: "6809867433" },
    "clp-end": { mobile: "8718859835", desktop: "7242126631" },
    "clp-top-both": { mobile: "8286600639+6754983760", desktop: "6809867433+6754983760" },
    "clp-end-both": { mobile: "8718859835+6754983760", desktop: "7242126631+6754983760" },
};

const shoppingUnitMap = {
    "feed-classic": {
        mobile: { styleId: "6877125116", numberOfProducts: 11 },
        tablet: { styleId: "4396941166", numberOfProducts: 3 },
        bigtablet: { styleId: "6457866956", numberOfProducts: 3 },
        netbook: { styleId: "6775628563", numberOfProducts: 4 },
        desktop: { styleId: "2605215193", numberOfProducts: 4 },
    },
    "feed-2020": {
        mobile: { styleId: "6493972874", numberOfProducts: 11 },
        tablet: { styleId: "8443912564", numberOfProducts: 3 },
        bigtablet: { styleId: "2062292250", numberOfProducts: 3 },
        netbook: { styleId: "3067548115", numberOfProducts: 4 },
        desktop: { styleId: "6244568227", numberOfProducts: 4 },
    },
    "pdp-classic": {
        mobile: { styleId: "4342967516", numberOfProducts: 11 },
        tablet: { styleId: "4342967516", numberOfProducts: 4 },
        desktop: { styleId: "2762612834", numberOfProducts: 5 },
    },
    "pdp-2020": {
        mobile: { styleId: "1355743730", numberOfProducts: 11 },
        tablet: { styleId: "1355743730", numberOfProducts: 4 },
        desktop: { styleId: "5047513430", numberOfProducts: 5 },
    },
};

export default baustein.register("adsense-custom-search-ads", {
    init() {
        googleCsa();
    },

    onInsert() {
        // modules with 'position' set are triggered explicitly
        if (!this.options.dataPosition) {
            this._removeVisiblityWatcher = addVisibilityWatcher(this.el, (visible) => {
                if (visible) {
                    if (this.el.querySelector(".adsense-shopping-container")) {
                        this.createCsaShoppingUnit();
                    } else {
                        this.createCsaUnit();
                    }

                    this._removeVisiblityWatcher();
                }
            });
        }
    },

    createCsaUnit() {
        const csaContainer = this.el.querySelector(".adsense-container");
        const { keyword, pagetype } = this.options;

        const channelId = getFromDeviceMap(channelIdMap, pagetype);

        const pageOptions = {
            pubId: "pub-6862407432380262",
            query: keyword,
            adPage: 1,
            location: false,
            siteLinks: showSitelinks(pagetype),
            channel: channelId,
        };

        const adblock1 = {
            container: csaContainer.id,
            width: "100%",
            maxTop: 2,
            fontFamily: "helvetica neue",
            colorTitleLink: "021135",
            colorDomainLink: "707070",
            colorText: "021135",
            adLoadedCallback: function(containerName, adsLoaded) {
                if (adsLoaded) {
                    try {
                        onAdLoaded(csaContainer, "adsense", channelId);
                    } catch (err) {
                        analytics.event("adsense", "no_ad", channelId, true);
                    }
                }
            },
        };

        googleCsa("ads", pageOptions, adblock1);
        analytics.event("adsense", "impression", channelId, true);
    },

    createCsaShoppingUnit() {
        const csaContainer = this.el.querySelector(".adsense-shopping-container");
        const { channelId, styleId: lystStyleId, keyword, personalizedAds } = this.options;
        const { styleId: googleStyleId, numberOfProducts } = getFromDeviceMap(
            shoppingUnitMap,
            lystStyleId
        );

        const pageOptions = {
            pubId: "partner-vert-pla-lyst-srp",
            styleId: googleStyleId,
            query: keyword,
            personalizedAds: personalizedAds == "True",
            channel: channelId,
            hl: getLanguageWithoutCountry(),
            adtest: isPreviewDomain() ? "on" : "off",
        };

        const afshBlock = {
            container: csaContainer.id,
            width: csaContainer.offsetWidth,
            number: numberOfProducts,
            enableInteractive: true,
            linkTarget: "_blank",
            adLoadedCallback: function(containerName, adsLoaded) {
                if (adsLoaded) {
                    try {
                        onAdLoaded(csaContainer, "adsense-shopping", channelId, {
                            personalizedAds,
                        });
                    } catch (err) {
                        analytics.event("adsense-shopping", "no_ad", channelId, true, {
                            personalizedAds,
                        });
                    }
                }
            },
        };

        googleCsa("plas", pageOptions, afshBlock);
        analytics.event("adsense-shopping", "impression", channelId, true, { personalizedAds });
    },
});

function isPreviewDomain() {
    return globals.window.location.hostname.split(".").indexOf("lystpreviews") > 0;
}

export function getFromDeviceMap(map, pagetype) {
    const deviceMap = map[pagetype];
    const windowWidth = browser.getClientWidth();

    // NB. not BREAKPOINTS.ExtraLarge, a bit bigger than that (1200px)
    if (windowWidth >= 1305) {
        // optional desktop breakpoint
        return deviceMap["desktop"];
        // NB. not BREAKPOINTS.Large, a tiny big bigger (992px)
    } else if (windowWidth >= 1021) {
        // optional netbook breakpoint - defaults to desktop
        if ("netbook" in deviceMap) {
            return deviceMap["netbook"];
        }
        return deviceMap["desktop"];
    } else if (windowWidth >= 992) {
        // optional breakpoint - defaults to desktop
        if ("bigtablet" in deviceMap) {
            return deviceMap["bigtablet"];
        }
        if ("tablet" in deviceMap) {
            return deviceMap["tablet"];
        }
        return deviceMap["desktop"];
    } else if (windowWidth >= BREAKPOINTS.Medium) {
        // optional tablet breakpoint - defaults to desktop
        if ("tablet" in deviceMap) {
            return deviceMap["tablet"];
        }
        return deviceMap["desktop"];
    } else {
        // all maps should support mobile
        return deviceMap["mobile"];
    }
}

function showSitelinks(pagetype) {
    if (pagetype && pagetype === "instock-pdp") {
        return true;
    }
    return false;
}
